import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { processError } from "../../../../common/utils/base_error";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { useProjetoRevisaoDisciplinaFilters } from "../hooks/Revisao_cadastro/filter";
import { ProjetoRevisaoModel } from "../../domain/models/revisao_projeto";
import { defaultProjetoRevisaoCadastroForm, ProjetoRevisaoCadastroForm, ProjetoRevisaoCadastroSchema } from "../schemas/Revisao_cadastro/RevisaoCadastroSchema";
import { useForm } from "react-hook-form";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";


export type Disciplinas = {
  idDisciplina: number;
  descricao: string;

}

type LoadDisciplinasSubdiciplinasFuncState = {
  idProjeto?: number,
  tipo?: number | null,
  prancha?: string | null,
  revisao?: string | null,
  descricao?: string | null,

};
export const useRevisaoCadastroGerenciarViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      getProjetosDropDownUseCase,

    },

    projetos: {
      getProjetoRevisaoByIdUseCase,
      updateProjetoRevisaoUseCase,
      getDisciplinasSubdisciplinasByProjetoUseCase,
      downloadProjetoRevisaoArquivoUseCase,
    },

  } = useDependencyInjection();

  const location = useLocation();
  const navigate = useNavigate();

  const filters = useProjetoRevisaoDisciplinaFilters();

  const { pushErrorNotification, pushInfoNotification, pushWarnNotification, setCustomLoading } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [disciplinasCadastradas, setDisciplinasCadastradas] = useState<Disciplinas[]>([]);
  const [disciplinasNaoCadastradas, setDisciplinasNaoCadastradas] = useState<Disciplinas[]>([]);

  const [ddlProjetos, setDDlProjetos] = useState<DropDown[]>();
  const [selectedProjetoRevisao, setSelectedProjetoRevisao] = useState<ProjetoRevisaoModel>();

  const hookForm = useForm<ProjetoRevisaoCadastroForm>({
    reValidateMode: "onSubmit",
    mode: "onSubmit",
    defaultValues: defaultProjetoRevisaoCadastroForm,
    resolver: zodResolver(ProjetoRevisaoCadastroSchema),
  });

  const selectedIdCentroCusto = hookForm.watch("idCentroCusto");
  const selectedIdProjeto = hookForm.watch("idProjeto");

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  //#region CARREGAMENTO 

  const loadDropdownProjetos = useCallback(async (idCentroCusto?: number) => {
    try {
      setLoading(true);
      const result = await getProjetosDropDownUseCase.execute(idCentroCusto);
      setDDlProjetos(result);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar projetos!"));
      setDDlProjetos([]);
    } finally {
      setLoading(false);
    }
  }, [
    getProjetosDropDownUseCase,
    pushErrorNotification
  ]);

  const loadProjetoRevisao = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const result = await getProjetoRevisaoByIdUseCase.execute(id);
        setSelectedProjetoRevisao(result);

        hookForm.setValue("idProjetoRevisao", result.idProjetoRevisao);
        hookForm.setValue("idProjeto", result.idProjeto);
        hookForm.setValue("idCentroCusto", result.idCentroCusto ?? 0);
        hookForm.setValue("idFuncionarioSolicitante", result.idFuncionarioSolicitante ?? 0);
        hookForm.setValue("dataSolicitacao", result.dataSolicitacao ? new Date(result.dataSolicitacao) : undefined);
        hookForm.setValue("observacoesSolicitacao", result.observacoesSolicitacao ?? undefined);
        hookForm.setValue("observacoesAprovacao", result.observacoesAprovacao ?? undefined);

        hookForm.setValue("idFuncionarioAprovacao", result.idFuncionarioAprovacao ?? 0);
        hookForm.setValue("dataAprovacao", result.dataAprovacao ? new Date(result.dataAprovacao) : undefined);
        hookForm.setValue("dataTermino", result.dataTermino ? new Date(result.dataTermino) : undefined);
        hookForm.setValue("status", result.status ?? undefined);

        hookForm.setValue("nomeArquivo1", result.nomeArquivo1 ?? undefined);
        hookForm.setValue("arquivo1", null);
        hookForm.setValue("nomeArquivo2", result.nomeArquivo2 ?? undefined);
        hookForm.setValue("arquivo2", null);
        hookForm.setValue("disciplinasIds", result.disciplinasIds ?? undefined);

        await loadDropdownProjetos(result.idCentroCusto ?? undefined);

      } catch (e: unknown) {
        setErrorMessage(processError(e, "Falha ao carregar revisão")[0]);
      } finally {
        setLoading(false);
      }
    },
    [
      getProjetoRevisaoByIdUseCase,
      loadDropdownProjetos,
      hookForm,
    ],
  );



  const loadDisciplinasSubdisciplinas = useCallback(async (funcState: LoadDisciplinasSubdiciplinasFuncState) => {

    try {
      setLoading(true);
      const filteredTipo = evaluateParamValue(funcState?.tipo, filters.tipo);
      const filteredPrancha = evaluateParamValue(funcState?.prancha, filters.prancha);
      const filteredRevisao = evaluateParamValue(funcState?.revisao, filters.revisao);
      const filteredDescricao = evaluateParamValue(funcState?.descricao, filters.descricao);

      if (!selectedIdProjeto) {
        pushWarnNotification("Atenção", "Selecione um projeto para carregar as disciplinas/subdisciplinas!");
        setDisciplinasCadastradas([]);
        setDisciplinasNaoCadastradas([]);
        return;
      }

      const data = await getDisciplinasSubdisciplinasByProjetoUseCase.execute(
        selectedIdProjeto,
        filteredTipo ?? 2,
        filteredPrancha,
        filteredRevisao,
        filteredDescricao
      );

      if (selectedProjetoRevisao) {
        const disciplinasIds = selectedProjetoRevisao.disciplinasIds ?? [];
        setDisciplinasCadastradas(data.filter(x => disciplinasIds.includes(x.idDisciplina)));
        setDisciplinasNaoCadastradas(data.filter(x => !disciplinasIds.includes(x.idDisciplina)));
      } else {
        setDisciplinasCadastradas([]);
        setDisciplinasNaoCadastradas(data);
      }

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar disciplinas/subdisciplinas!"));
    } finally {
      setLoading(false);
    }
  }, [
    getDisciplinasSubdisciplinasByProjetoUseCase,
    selectedProjetoRevisao,
    selectedIdProjeto,

    filters,
    pushErrorNotification,
    pushWarnNotification,
  ]
  );

  //#endregion



  //#region PAGE START

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id");
      const id = locationId ?? undefined;

      if (!id || id.length === 0) {
        setErrorMessage("Falha ao recuperar id do checklist!");
        return;
      }

      await loadProjetoRevisao(Number(locationId));
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das páginas!")[0]);

    }
  });

  useEffect(() => {
    if (selectedIdCentroCusto) {
      (async () => {
        await loadDropdownProjetos(selectedIdCentroCusto);
      })();
    }

    if (selectedIdProjeto) {
      (async () => {
        await loadDisciplinasSubdisciplinas({ idProjeto: selectedIdProjeto, tipo: 2 });
      })();
    }

  }, [
    selectedIdProjeto,
    selectedIdCentroCusto,
    loadDropdownProjetos,
    loadDisciplinasSubdisciplinas
  ]);

  //#endregion



  //#region UPDATE


  const handleSubmitProjetoRevisao = useCallback(async (form: ProjetoRevisaoCadastroForm) => {
    try {
      setSaving(true);
      if (selectedProjetoRevisao) {
        await updateProjetoRevisaoUseCase.execute(
          form.idProjetoRevisao!,
          form.status,
          form.idFuncionarioAprovacao,
          form.observacoesAprovacao,
          form.dataAprovacao ?? undefined,
          form.dataTermino ?? undefined,
        );

        await loadProjetoRevisao(selectedProjetoRevisao.idProjetoRevisao);
        pushInfoNotification("Sucesso", "Dados salvos com sucesso");
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao salvar revisão!")[0]);
    } finally {
      setSaving(false);
    }
  }, [
    selectedProjetoRevisao,
    updateProjetoRevisaoUseCase,
    loadProjetoRevisao,
    pushInfoNotification,
  ]);

  //#endregion



  //#region MUDANÇA DE PROJETOS


  const handleChangeDisciplinasLista = useCallback((src: Disciplinas[], selected: Disciplinas[]) => {
    setDisciplinasNaoCadastradas(src);
    setDisciplinasCadastradas(selected);

    hookForm.setValue('disciplinasIds', selected.map(x => x.idDisciplina));
  }, [
    hookForm,
  ]);


  //#endregion


  //#region  ARQUIVOS

  const handleDownloadArquivo = useCallback(async (nomeArquivo: string, numeroArquivo: number) => {
    try {
      setCustomLoading(true, "Baixando arquivo...");
      if (!selectedProjetoRevisao) {
        pushErrorNotification("Erro", ["Falha ao baixar arquivo!"], { isAutoFinish: true });
        return;
      }
      await downloadProjetoRevisaoArquivoUseCase
        .execute(
          selectedProjetoRevisao.idProjetoRevisao,
          numeroArquivo,
          nomeArquivo);

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao baixar arquivo!"));
    } finally {
      setCustomLoading(false);
      setLoading(false);
    }
  }, [
    selectedProjetoRevisao,
    downloadProjetoRevisaoArquivoUseCase,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleDeleteFile = useCallback(() => {
    if (!saving) {
      if (window.confirm('Você deseja excluir este item?')) {
        hookForm.setValue('nomeArquivo1', null);
        hookForm.setValue('arquivo1', null);
        setSelectedProjetoRevisao({
          ...selectedProjetoRevisao,
          idProjetoRevisao: selectedProjetoRevisao?.idProjetoRevisao ?? 0,
          idProjeto: selectedProjetoRevisao?.idProjeto ?? 0,
          idCentroCusto: selectedProjetoRevisao?.idCentroCusto ?? 0,
          arquivo1: undefined,
          nomeArquivo1: undefined,
        });
      }
    }
  }, [
    saving,
    hookForm,
    selectedProjetoRevisao,
  ]);

  //#endregion



  //#region CABEÇALHO

  const handlePageBack = useCallback(() => {
    navigate('/Revisao_listagerenciar')

  }, [navigate]);


  const handleReloadPage = () => {
    hookForm.reset();
    const params = new URLSearchParams(location.search);
    const locationId = params.get("id");
    if (locationId) loadProjetoRevisao(Number(locationId));

  };

  //#endregion

  //#region  Historico

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (selectedProjetoRevisao) {
          changeHistory.handleOpenChangeHistory(selectedProjetoRevisao.idProjetoRevisao, LogTipoTabela.PROJETO_REVISAO, true);
        }
      }
    },
    [changeHistory, selectedProjetoRevisao],
  );

  //#endregion

  return {
    loading,
    selectedProjetoRevisao,
    disciplinasCadastradas,
    disciplinasNaoCadastradas,
    filters,
    hookForm,
    errorMessage,
    ddlProjetos,
    saving,
    changeHistory,

    handleOpenChangeHistory,
    handleChangeDisciplinasLista,
    handleDeleteFile,
    handleSubmitProjetoRevisao: hookForm.handleSubmit(handleSubmitProjetoRevisao),
    handlePageBack,
    handleReloadPage,
    handleDownloadArquivo,
  };
};
